import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { PATIENT_LIST_PATH } from '~/components/Header/useReturnToPatientList';
import { useSessionOrNull } from '~/contexts/SessionContext';
import type { PatientSession } from '~/domain/session/PatientSession';
import type {
    SaveOutcome,
    SaveReport,
} from '~/domain/services/save/SaveOutcome';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { pendingReopenRequestState } from '~/hooks/openInViewer/pendingReopenAtom';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import { runSaveAttempt } from '~/backend/connector-next-api/saveHttp';
import { saveReportState, patientSavingState } from '~/state/atoms/saveAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { redactDicomUids } from '~/utils/phiRedaction';

export type SaveConflict = Extract<SaveOutcome, { kind: 'conflict' }> & {
    /** For the conflict dialog — never logged. */
    readonly displayName: string;
};

export type SavePatientChanges = {
    readonly saving: boolean;
    /** Last report, cleared when a new save starts. */
    readonly report: SaveReport | null;
    /** Conflicts of the last report, dialog-ready. */
    readonly conflicts: readonly SaveConflict[];
    /** Resolves with the report (null on orchestration failure). */
    readonly saveAll: () => Promise<SaveReport | null>;
    /** Acknowledged overwrite retry for the given conflicted datasets. */
    readonly overwrite: (ids: readonly string[]) => Promise<SaveReport | null>;
    /** Discard local changes and re-open the patient from the database. */
    readonly discardAndReload: () => void;
    readonly clearConflicts: () => void;
};

/**
 * Drives the save workflow from the UI: flushes the engine into the
 * domain, runs {@link PatientSaveService.saveAll} across every dataset
 * kind, and reports outcomes per dataset — never a blanket "saved" when
 * something failed or conflicted.
 */
export function useSavePatientChanges(): SavePatientChanges {
    const session = useSessionOrNull();
    const setPendingReopen = useSetRecoilState(pendingReopenRequestState);
    const { unloadCurrentPatient } = useUnloadPatient();
    const navigate = useNavigate();
    const intl = useIntl();

    // Shared across hook instances (see saveAtoms): the header indicator
    // and the patient menu's save-and-close observe the SAME save, so
    // conflicts always surface in the one dialog the indicator renders.
    const [saving, setSaving] = useRecoilState(patientSavingState);
    const [report, setReport] = useRecoilState(saveReportState);

    const notifyReport = useCallback(
        (result: SaveReport) => {
            const saved = result.outcomes.filter((o) => o.kind === 'saved');
            const failed = result.outcomes.filter((o) => o.kind === 'failed');
            const conflicted = result.outcomes.filter(
                (o) => o.kind === 'conflict'
            );
            if (result.allSaved) {
                enqueueSnackbar(
                    intl.formatMessage({ id: 'saveChanges.savedAll' }),
                    { variant: 'success' }
                );
                return;
            }
            if (saved.length > 0) {
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'saveChanges.savedPartial' },
                        { count: saved.length }
                    ),
                    { variant: 'info' }
                );
            }
            if (failed.length > 0) {
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'saveChanges.failed' },
                        { count: failed.length }
                    ),
                    { variant: 'error' }
                );
            }
            if (conflicted.length > 0) {
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'saveChanges.conflicted' },
                        { count: conflicted.length }
                    ),
                    { variant: 'warning' }
                );
            }
        },
        [intl]
    );

    const run = useCallback(
        async (
            overwrite: ReadonlySet<string> | undefined
        ): Promise<SaveReport | null> => {
            if (!session || saving) {
                return null;
            }
            setSaving(true);
            setReport(null);
            try {
                // One save attempt = one attempt id on every request it
                // makes, so the connector log shows one clinical action.
                // The engine flush covers every grid-resident Environment,
                // so no contour edit is missing from the sealed payload and
                // a set that lost edits is refused.
                const result = await runSaveAttempt(() =>
                    session.patientSaveService.saveAll({ overwrite }, () =>
                        getDatasetEnvironmentRegistry().saveAllResidentChanges()
                    )
                );
                setReport(result);
                notifyReport(result);
                return result;
            } catch (error) {
                // Unexpected orchestration failure (not a per-set outcome):
                // report and keep everything pending.
                // A failed grid flush carries series UIDs.
                console.error('Save failed:', redactDicomUids(error));
                enqueueSnackbar(
                    intl.formatMessage({ id: 'saveChanges.saveError' }),
                    { variant: 'error' }
                );
                return null;
            } finally {
                setSaving(false);
            }
        },
        [session, saving, setSaving, setReport, notifyReport, intl]
    );

    const saveAll = useCallback(() => run(undefined), [run]);
    const overwrite = useCallback(
        (ids: readonly string[]) => run(new Set(ids)),
        [run]
    );

    const discardAndReload = useCallback((): void => {
        const patientId = session?.persistenceRegistry.patientRef?.patientId;
        if (!patientId) {
            return;
        }
        // Reuses the standard retrieval pipeline: unload, return to the
        // worklist, and let OpenInViewerDownloader consume the reopen
        // request on mount. No parallel reload path.
        unloadCurrentPatient();
        setPendingReopen({ patientId });
        navigate(PATIENT_LIST_PATH);
    }, [session, unloadCurrentPatient, setPendingReopen, navigate]);

    const conflicts: SaveConflict[] = (report?.outcomes ?? [])
        .filter(
            (o): o is Extract<SaveOutcome, { kind: 'conflict' }> =>
                o.kind === 'conflict'
        )
        .map((o) => ({
            ...o,
            displayName: conflictDisplayName(session, o),
        }));

    const clearConflicts = useCallback(() => setReport(null), [setReport]);

    function conflictDisplayName(
        liveSession: PatientSession | null,
        outcome: Extract<SaveOutcome, { kind: 'conflict' }>
    ): string {
        if (!liveSession) {
            return '';
        }
        const pending = liveSession.pendingChangesStore.getState();
        if (outcome.entity === 'structureSet') {
            return (
                liveSession.patient.getStructureSetById(outcome.id)
                    ?.structureSetLabel ??
                pending.byStructureSet.get(
                    outcome.id as Parameters<
                        typeof pending.byStructureSet.get
                    >[0]
                )?.displayName ??
                ''
            );
        }
        if (outcome.entity === 'registration') {
            return (
                liveSession.patient.registrations.find(
                    (reg) => reg.id === outcome.id
                )?.contentLabel ??
                pending.byRegistration.get(
                    outcome.id as Parameters<
                        typeof pending.byRegistration.get
                    >[0]
                )?.displayName ??
                ''
            );
        }
        if (outcome.entity === 'image') {
            // Image renames are version-guarded and can 409-stale.
            return (
                liveSession.patient.images.find(
                    (image) => image.id === outcome.id
                )?.seriesMetadata.seriesDescription ??
                pending.byImage.get(
                    outcome.id as Parameters<typeof pending.byImage.get>[0]
                )?.displayName ??
                ''
            );
        }
        // Dose deletes are unguarded and cannot conflict; fall back to
        // the empty name if that ever changes before the UI does.
        return '';
    }

    return {
        saving,
        report,
        conflicts,
        saveAll,
        overwrite,
        discardAndReload,
        clearConflicts,
    };
}
